import {useCallback, useState} from 'react';
import {useLocation} from 'react-router';
import {useHistory} from 'react-router-dom';

import {useDebouncedValue} from '../../../../util/useDebouncedValue';

type UseUpdateChartTitleProps = {
  defaultChartTitle: string;
  chartTitle: string | undefined;
  unsetConfigField: () => void;
  updateConfig: (title: string) => void;
};

export function useUpdateChartTitle({
  defaultChartTitle,
  chartTitle,
  unsetConfigField,
  updateConfig,
}: UseUpdateChartTitleProps) {
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);

  // Debounce the updateConfig call while the user is typing
  const updateChartTitle = useCallback(
    (chartTitle: string) => {
      const panelDisplayName = searchParams.get('panelDisplayName');
      if (chartTitle === '') {
        unsetConfigField();
        // Update the URL for the panel display name if on a specific run page
        if (panelDisplayName && panelDisplayName !== defaultChartTitle) {
          searchParams.set('panelDisplayName', defaultChartTitle);
          history.replace(`${location.pathname}?${searchParams.toString()}`);
        }
      } else {
        updateConfig(chartTitle);

        // Update the URL for the panel display name if on a specific run page
        if (panelDisplayName && panelDisplayName !== chartTitle) {
          searchParams.set('panelDisplayName', chartTitle);
          history.replace(`${location.pathname}?${searchParams.toString()}`);
        }
      }
    },
    // If we rely on searchParams, updateConfig will be called on every keystroke
    // because the useCallback will be regenerated and break the debouncing
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [updateConfig, location.pathname, unsetConfigField, defaultChartTitle]
  );
  const {
    value: configChartTitle,
    debouncedSetValue: debouncedUpdateChartTitle,
  } = useDebouncedValue(chartTitle ?? '', 2000, updateChartTitle);

  // this state is needed to control the input field - it is updated with every keystroke
  const [chartTitleState, setChartTitleState] = useState(configChartTitle);

  const onChange = useCallback(
    (newValue: string) => {
      setChartTitleState(newValue);
      debouncedUpdateChartTitle(newValue);
    },
    [debouncedUpdateChartTitle]
  );
  const resetValue = useCallback(() => {
    setChartTitleState('');
    debouncedUpdateChartTitle('');
  }, [debouncedUpdateChartTitle]);

  return {value: chartTitleState, onChange, resetValue};
}
